import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import type { ActualizarPerfilBody, ModuloId, Perfil } from '../../negocio/negocio.types';
import { PerfilesService } from '../services/perfiles.service';
import { ETIQUETA_MODULO, GRUPOS_MODULOS, MODULOS_SENSIBLES, ordenarModulos, validarPerfil } from '../utils/perfiles';
import { mensajeDeError } from '../../negocio/errores';

interface Props {
  /** `null` = crear uno nuevo. */
  perfil: Perfil | null;
  onHide: () => void;
  onGuardado: (p: Perfil) => void;
}

export const CLASE_CAMPO =
  'mt-1 w-full rounded-md border border-solid border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500';

/**
 * Crear o editar un perfil de Consulta CM (fase 3). Un perfil es una lista de
 * MÓDULOS: sólo quita (una ruta pasa si el perfil trae alguno de sus módulos).
 *
 * - La clave no cambia al editar (la API responde 400 si llega).
 * - Los de fábrica se editan o se desactivan, pero no se borran.
 * - «Utilidad y margen» exige además la llave maestra, apagada para todos.
 */
export const PerfilDialog: React.FC<Props> = ({ perfil, onHide, onGuardado }) => {
  const creando = !perfil;
  const [clave, setClave] = useState(perfil?.clave ?? '');
  const [nombre, setNombre] = useState(perfil?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(perfil?.descripcion ?? '');
  const [modulos, setModulos] = useState<ModuloId[]>(perfil ? ordenarModulos(perfil.modulos) : []);
  const [activo, setActivo] = useState(perfil?.activo ?? true);
  const [errores, setErrores] = useState<string[]>([]);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    setClave(perfil?.clave ?? '');
    setNombre(perfil?.nombre ?? '');
    setDescripcion(perfil?.descripcion ?? '');
    setModulos(perfil ? ordenarModulos(perfil.modulos) : []);
    setActivo(perfil?.activo ?? true);
    setErrores([]);
  }, [perfil]);

  const alternar = (m: ModuloId) =>
    setModulos((xs) => ordenarModulos(xs.includes(m) ? xs.filter((x) => x !== m) : [...xs, m]));

  const guardar = async () => {
    const e = validarPerfil({ clave: clave.trim(), nombre, modulos }, creando);
    setErrores(e);
    if (e.length) return;
    setOcupado(true);
    const desc = descripcion.trim() ? descripcion.trim() : null;
    try {
      let r: Perfil;
      if (creando) {
        r = await PerfilesService.crear({ clave: clave.trim(), nombre: nombre.trim(), descripcion: desc, modulos });
      } else {
        const body: ActualizarPerfilBody = { nombre: nombre.trim(), descripcion: desc, modulos };
        // `activo` sólo viaja si cambió: desactivar le quita TODOS los módulos a sus cuentas.
        if (activo !== perfil.activo) body.activo = activo;
        r = await PerfilesService.actualizar(perfil.id, body);
      }
      onGuardado(r);
    } catch (err) {
      setErrores([mensajeDeError(err)]);
    } finally {
      setOcupado(false);
    }
  };

  const conUtilidad = modulos.some((m) => MODULOS_SENSIBLES.includes(m));

  return (
    <Dialog
      header={creando ? 'Crear perfil' : `Editar perfil · ${perfil.nombre}`}
      visible
      onHide={onHide}
      style={{ width: '36rem' }}
      breakpoints={{ '640px': '95vw' }}
      dismissableMask={!ocupado}
      footer={
        <div className="flex justify-end gap-2">
          <Button label="Cancelar" className="p-button-text" onClick={onHide} disabled={ocupado} />
          <Button
            label={creando ? 'Crear perfil' : 'Guardar cambios'}
            icon="pi pi-check"
            onClick={() => void guardar()}
            loading={ocupado}
            disabled={ocupado}
          />
        </div>
      }
    >
      <div className="space-y-4 text-sm text-gray-700">
        {perfil?.deFabrica && (
          <p className="rounded-md bg-gray-100 px-3 py-2 text-xs text-gray-600">
            De fábrica: se edita o se desactiva, pero no se borra.
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block font-medium" htmlFor="perfil-clave">
            Clave
            <input
              id="perfil-clave"
              className={CLASE_CAMPO}
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              disabled={!creando || ocupado}
              placeholder="compras-tre"
              maxLength={32}
            />
          </label>
          <label className="block font-medium" htmlFor="perfil-nombre">
            Nombre
            <input
              id="perfil-nombre"
              className={CLASE_CAMPO}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={ocupado}
              maxLength={80}
            />
          </label>
        </div>
        <label className="block font-medium" htmlFor="perfil-descripcion">
          Descripción (opcional)
          <textarea
            id="perfil-descripcion"
            className={CLASE_CAMPO}
            rows={2}
            maxLength={200}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            disabled={ocupado}
          />
        </label>

        <fieldset className="space-y-3">
          <legend className="font-medium">Módulos</legend>
          {GRUPOS_MODULOS.map((g) => (
            <div key={g.titulo}>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{g.titulo}</p>
              <div className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
                {g.modulos.map((m) => (
                  <label key={m} className="flex items-center gap-2" htmlFor={`modulo-${m}`}>
                    <input
                      id={`modulo-${m}`}
                      type="checkbox"
                      checked={modulos.includes(m)}
                      onChange={() => alternar(m)}
                      disabled={ocupado}
                    />
                    {ETIQUETA_MODULO[m]}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </fieldset>
        {conUtilidad && (
          <div className="rounded-md border border-solid border-amber-300 bg-amber-50 p-3 text-amber-900">
            ⚠️ La utilidad está apagada para todos hasta que Finanzas la valide: además de este módulo hace falta la
            llave maestra de la API. Mientras tanto, este perfil no ve utilidad ni margen.
          </div>
        )}

        {!creando && (
          <label className="flex items-center gap-2 font-medium" htmlFor="perfil-activo">
            <input
              id="perfil-activo"
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              disabled={ocupado}
            />
            Activo
            {!activo && perfil.usuarios > 0 && (
              <span className="font-normal text-amber-800">
                · sus {perfil.usuarios} cuenta{perfil.usuarios === 1 ? '' : 's'} se quedan sin módulos
              </span>
            )}
          </label>
        )}

        {errores.map((e) => (
          <Message key={e} severity="error" text={e} className="w-full justify-start" />
        ))}
      </div>
    </Dialog>
  );
};
